import { createReducer, on } from '@ngrx/store';

import { goToProjects, setProject } from '../../core/store';
import {
	fetchConsumedProfileColumnsSuccess,
	fetchConsumedProjectsSuccess,
	fetchMembersAccessSuccess,
	fetchProjectsSuccess,
	fetchPublicationHistoriesSuccess,
	fetchSubscriptionColumnModelsSuccess,
	fetchSubscriptionColumnsSuccess,
	fetchSubscriptionFilterModelsSuccess,
	fetchSubscriptionProfilesSuccess,
	fetchSubscriptionsSuccess,
	fetchSubscriptionSuccess,
	fetchSwitchableConsumedProjectsSuccess,
	hasSubscriptionCreatePermissionSuccess,
	initializeSubscription,
	refreshPublishedSubscriptionSuccess,
	refreshSubscriptionsSuccess,
	reloadProfilesAfterActionSuccess,
	resetSubscriptionView,
	switchVersionForConsumedProjects,
	switchVersionForConsumedProjectsFail,
	switchVersionForConsumedProjectsOnView,
	switchVersionForConsumedProjectsOnViewFail,
	switchVersionForConsumedProjectsOnViewSuccess,
	switchVersionForConsumedProjectsSuccess,
	updateInternalMetadataSuccess,
	updatePublicMetadataSuccess,
	updateSubscriptionActionInProgress,
	updateSubscriptionData,
} from './subscription.actions';
import { SubscriptionOriginalSliceState } from './subscription.state';

export const subscriptionReducer =
	createReducer<SubscriptionOriginalSliceState>(
		new SubscriptionOriginalSliceState(),
		on(resetSubscriptionView, (state) => state.resetSubscription()),
		on(hasSubscriptionCreatePermissionSuccess, (state, action) =>
			state.setHasCreatePermission(action.canCreate)
		),
		on(fetchSubscriptionsSuccess, (state, action) =>
			state
				.setSubscriptions(action.subscriptions)
				.setTotalItems(action.total)
				.setLoading(false)
		),
		on(refreshSubscriptionsSuccess, (state, action) =>
			state.setSubscriptions(action.subscriptions).setLoading(false)
		),
		on(fetchSubscriptionSuccess, (state, action) =>
			state.setInViewSubscription(action.subscription).setLoading(false)
		),
		on(refreshPublishedSubscriptionSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setPublished(action.publishedSubscription)
			)
		),
		on(updateInternalMetadataSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setInternalMetadata(action.internalMetadata)
			)
		),
		on(updatePublicMetadataSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setPublicMetadata(action.publicMetadata)
			)
		),
		on(
			fetchSubscriptionProfilesSuccess,
			reloadProfilesAfterActionSuccess,
			(state, action) =>
				state.setInViewSubscription(
					state.inViewSubscription.setProfiles(action.profiles)
				)
		),
		on(fetchSubscriptionColumnsSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setAvailableColumns(action.columns)
			)
		),
		on(fetchSubscriptionColumnModelsSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setColumnModels(action.columnModels)
			)
		),
		on(fetchSubscriptionFilterModelsSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setFilterModels(action.filterModels)
			)
		),
		on(fetchConsumedProjectsSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setConsumedProjects(action.consumedProjects)
			)
		),
		on(fetchConsumedProfileColumnsSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.updateConsumedProjectListWithColumns(
					action.consumerId,
					action.profileColumns
				)
			)
		),
		on(fetchSwitchableConsumedProjectsSuccess, (state, action) =>
			state.setSwitchableConsumedProjects(action.projects)
		),
		on(
			switchVersionForConsumedProjectsOnView,
			switchVersionForConsumedProjects,
			(state) => state.setIsSwitchingVersion(true)
		),
		on(
			switchVersionForConsumedProjectsOnViewSuccess,
			switchVersionForConsumedProjectsOnViewFail,
			switchVersionForConsumedProjectsSuccess,
			switchVersionForConsumedProjectsFail,
			(state) => state.setIsSwitchingVersion(false)
		),
		on(fetchProjectsSuccess, (state, action) =>
			state.setProjects(action.projects)
		),
		on(updateSubscriptionActionInProgress, (state, action) =>
			state.setSubscriptionActionInProgress(
				action.subscriptionId,
				action.actionInProgress
			)
		),
		on(initializeSubscription, (state) =>
			state.setInViewSubscription(
				state.inViewSubscription.setActionInProgress('initialize')
			)
		),
		on(updateSubscriptionData, (state) =>
			state.setInViewSubscription(
				state.inViewSubscription
					.setActionInProgress('update-data')
					.setPublished(state.inViewSubscription.published.markAsWaiting())
			)
		),
		on(fetchPublicationHistoriesSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setHistory(action.histories)
			)
		),
		on(fetchMembersAccessSuccess, (state, action) =>
			state.setInViewSubscription(
				state.inViewSubscription.setMembersAccess(action.membersAccess)
			)
		),
		on(goToProjects, setProject, (state) => state.clear())
	);
